import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

import ContextMenu from 'dashboard/components/ContextMenu';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { MonoSpan } from 'dashboard/components/Typography';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import PlatformConnectionsModalContent from 'dashboard/components-lib/PlatformConnectionsModalContent/PlatformConnectionsModalContent';
import FormCloneCaseTemplate, {
  CloneCaseTemplateFormData,
} from 'dashboard/forms/FormCloneCaseTemplate';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Role } from 'dashboard/models/role';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

interface CaseTemplatesTableProps {
  caseTemplates: CaseTemplate[];
  roles: Role[];
  sortParams: SortParams;
  hasUsedFilters: boolean;
  atActiveCaseTemplateLimit: boolean;
  isLoading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
  archiveCaseTemplate: (caseTemplate: CaseTemplate) => Promise<void>;
  restoreCaseTemplate: (caseTemplate: CaseTemplate) => Promise<void>;
  cloneCaseTemplate: (token: string, formData: CloneCaseTemplateFormData) => Promise<void>;
}

const CaseTemplatesTable = ({
  caseTemplates,
  roles,
  sortParams,
  hasUsedFilters,
  atActiveCaseTemplateLimit,
  isLoading,
  handleSortUpdate,
  archiveCaseTemplate,
  restoreCaseTemplate,
  cloneCaseTemplate,
}: CaseTemplatesTableProps) => {
  const canEditCaseTemplate = usePermission(Permission.CaseTemplateWrite);
  const [showArchiveCaseTemplateModal, { prompt, confirm, cancel }] = useConfirmation();
  const [
    cloneCaseTemplateModal,
    { enable: showCloneCaseTemplateModal, disable: hideCloneCaseTemplateModal },
  ] = useToggle(false);

  const [caseTemplate, setCaseTemplate] = useState<CaseTemplate | undefined>(undefined);

  const handleCloneCaseTemplate = useCallback(
    async (formData: CloneCaseTemplateFormData) => {
      hideCloneCaseTemplateModal();
      if (caseTemplate) {
        await cloneCaseTemplate(caseTemplate.id, formData);
      }
    },
    [cloneCaseTemplateModal],
  );

  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const caseTemplateRows = caseTemplates.map((caseTemplate) => {
    const tableRowLink = `/dashboard/case-templates/${caseTemplate.id}`;

    const {
      name,
      'case-prefix': casePrefix,
      'updated-at': updatedAt,
      'initial-state-case-count': initialStateCaseCount,
      status,
    } = caseTemplate.attributes;
    const updatedAtDisplay = updatedAt
      ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)
      : '–';

    const handleArchiveCaseTemplateClick = () => prompt(() => archiveCaseTemplate(caseTemplate));
    const handleRestoreCaseTemplateClick = () => restoreCaseTemplate(caseTemplate);

    return (
      <TableLinkTR
        to={tableRowLink}
        data-test={`case-templates-table__row--${caseTemplate.id}`}
        id={`case-templates-table__row--${caseTemplate.id}`}
        key={caseTemplate.id}
      >
        <TableTD>{name}</TableTD>
        <TableTD>{casePrefix}</TableTD>
        <TableTD>{initialStateCaseCount}</TableTD>
        <TableTD>
          <HoverCopyableField
            copyValue={caseTemplate.id}
            confirmationText='Copied Case Template ID'
          >
            <MonoSpan>{caseTemplate.id}</MonoSpan>{' '}
          </HoverCopyableField>
        </TableTD>
        <TableTD>
          <CaseTemplateStatusSticker status={status} />
        </TableTD>
        <TableTD>{updatedAtDisplay}</TableTD>
        <TableActionTD>
          <ContextMenu
            data-test={`case-templates-table-row__action-menu--${caseTemplate.id}`}
            items={[
              {
                id: 'clone',
                onClick: () => {
                  setCaseTemplate(caseTemplate);
                  showCloneCaseTemplateModal();
                },
                hidden: !canEditCaseTemplate || atActiveCaseTemplateLimit,
                variant: 'basic' as const,
                label: 'Clone',
              },
              {
                id: 'restore',
                onClick: handleRestoreCaseTemplateClick,
                hidden: status !== CaseTemplateStatus.Archived || !canEditCaseTemplate,
                variant: 'basic' as const,
                label: 'Restore',
              },
              {
                id: 'archive',
                onClick: () => {
                  setCaseTemplate(caseTemplate);
                  handleArchiveCaseTemplateClick();
                },
                hidden: status !== CaseTemplateStatus.Active || !canEditCaseTemplate,
                variant: 'danger' as const,
                label: 'Archive',
              },
            ]}
            renderItem={(item) => (
              <ContextMenu.Item
                id={`case-templates-table__row--${caseTemplate.id}__${item.id}`}
                key={`case-templates-table__row--${caseTemplate.id}__${item.id}`}
                onClick={(e) => {
                  // Needed to avoid triggering row click below
                  e.stopPropagation();
                  e.preventDefault();
                  item.onClick?.();
                }}
                variant={item.variant}
                data-test={item['data-test']}
              >
                {item.label}
              </ContextMenu.Item>
            )}
            aria-label='More'
          />
        </TableActionTD>
      </TableLinkTR>
    );
  });

  return (
    <>
      <TableLayout
        dataTest='case-templates-table'
        isLoading={isLoading}
        showHeader={true}
        isEmpty={caseTemplates.length === 0}
        nullState={
          <TableNullState
            hasUsedFilters={hasUsedFilters}
            dataTestBlock='case-templates-table__null-state'
          >
            <h3>Looks like there aren&#39;t any case templates yet.</h3>
            <p>Create a case template to get started.</p>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH
              onClick={() => handleColumnHeaderClick('name')}
              canSort={true}
              isSorted={sortParams.sort === 'name'}
              isDescending={sortParams.shouldSortDescending}
              id='case-templates-table__name-header'
              isSticky
            >
              Name
            </TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('case_prefix')}
              canSort={true}
              isSorted={sortParams.sort === 'case_prefix'}
              isDescending={sortParams.shouldSortDescending}
              id='case-templates-table__key-header'
              isSticky
            >
              Key
            </TableTH>
            <TableTH isSticky>Open cases</TableTH>
            <TableTH isSticky>ID</TableTH>
            <TableTH isSticky>Status</TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('updated_at')}
              canSort={true}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              id='case-templates-table__updated-at-header'
              isSticky
            >
              {`Last updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
            <TableTH isSticky>{/* Actions column */}</TableTH>
          </>
        }
      >
        {caseTemplateRows}
      </TableLayout>
      <ModalWrapper isOpen={showArchiveCaseTemplateModal} handleCloseModal={cancel}>
        <ConfirmationModal
          size='lg'
          cancelAction={cancel}
          confirmAction={confirm}
          header='Archive this case template?'
          confirmActionText='Archive'
        >
          <p>
            You will no longer be able to create cases with this case template. Please ensure all
            references to this template are removed from your workflows.
          </p>

          <FeatureFlagged featureFlag='global:enable-platform-dependency-modal'>
            {caseTemplate && (
              <>
                <Spacer height={1} />
                <PlatformConnectionsModalContent platformNodeId={caseTemplate.id} />
              </>
            )}
          </FeatureFlagged>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={cloneCaseTemplateModal} handleCloseModal={hideCloneCaseTemplateModal}>
        <ModalContainer>
          <ModalHeader onClose={hideCloneCaseTemplateModal}>
            Clone {caseTemplate?.attributes?.name}
          </ModalHeader>
          <ModalBody>
            <FormCloneCaseTemplate
              handleCancel={hideCloneCaseTemplateModal}
              submit={handleCloneCaseTemplate}
              roles={roles}
              defaultValues={{
                name: caseTemplate?.attributes?.name + ' (copy)',
                'role-ids': roles.map((role) => role.id),
              }}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

const CaseTemplateStatusSticker = ({ status }: { status: CaseTemplateStatus }) => {
  if (status === CaseTemplateStatus.Active) {
    return <Sticker variant='success'>Active</Sticker>;
  } else {
    return <Sticker variant='basic'>Archived</Sticker>;
  }
};

export default CaseTemplatesTable;
